import cx from "classnames"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import Card from "components/Card"
import CardGroup from "components/CardGroup"
import { richTextToHtml } from "utils/richTextToHtml"
import * as css from "./Column2CardGroup.module.css"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"

const PRIMARY_LAYOUT_OPTION = "Primary"
const SECONDARY_LAYOUT_OPTION = "Secondary"

function Column2CardGroup({
  layout,
  heading,
  label,
  subHeading,
  description,
  boxItems,
}) {
  const isPrimaryLayout = () => !layout || layout === PRIMARY_LAYOUT_OPTION

  const isSecondaryLayout = () => layout === SECONDARY_LAYOUT_OPTION

  const renderCard = ({
    id,
    heading: head,
    description: descr,
    richText,
    logo,
    ctaLink,
    document,
  }) => {
    const richTextDoc = richText?.raw ? JSON.parse(richText?.raw) : null
    return (
      <Card
        className={css.card}
        href={document ? document.url : ctaLink}
        target={document ? "_blank" : null}
        key={id}
      >
        <div className={css.cardContent}>
          {logo && (
            <img src={logo.url} alt={head} className={css.image} width="110" />
          )}
          <div className="wrapper">
            <div className={css.cardHeader}>{head}</div>
            {descr && <div className={css.cardMeta}>{descr}</div>}
            {richTextDoc && (
              <div
                className={css.cardMeta}
                dangerouslySetInnerHTML={{
                  __html: richTextToHtml(richTextDoc).replace(/\n/g, `<br />`),
                }}
              />
            )}
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Section
      className={cx({
        [css.primaryLayout]: isPrimaryLayout(),
        [css.secondaryLayout]: isSecondaryLayout(),
      })}
    >
      {isPrimaryLayout() && (
        <SimpleHeader
          level="h1"
          label={label}
          title={heading}
          subtitle={subHeading}
          titleSize="xl"
          dash="horizontal"
          narrow="both"
          contrasted
        />
      )}
      {isSecondaryLayout() && (
        <SimpleHeader
          label={label}
          title={heading}
          subtitle={subHeading}
          dash="vertical"
          narrow="subtitle"
        />
      )}
      {description && <div className={css.description}>{description}</div>}
      <CardGroup className={css.cardGroup} itemsPerRow={2} stackable overflow>
        {boxItems?.map((item) => renderCard(item))}
      </CardGroup>
    </Section>
  )
}

Column2CardGroup.propTypes = {
  layout: PropTypes.oneOf([PRIMARY_LAYOUT_OPTION, SECONDARY_LAYOUT_OPTION]),
  label: PropTypes.string,
  heading: PropTypes.string,
  subHeading: PropTypes.string,
  boxItems: PropTypes.array,
}

export default Column2CardGroup

export const query = graphql`
  fragment Column2CardGroup on ContentfulColumn2CardGroup {
    id
    __typename
    layout
    heading
    label
    subHeading
    description
    boxItems {
      id
      heading
      description
      richText {
        raw
      }
      ctaLink
      document {
        url
      }
      logo {
        url
      }
    }
  }
`
